import { FC, ReactNode } from 'react';
import {
  Box,
  Button,
  IconButton,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

export interface ListPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  /** Defaults to `page > 1` when omitted. */
  hasPrevPage?: boolean;
  /** Defaults to `page < totalPages` when omitted. */
  hasNextPage?: boolean;
  /** Right-aligned summary (e.g. "Page 1 of 4 · 38 total"). */
  summary?: ReactNode;
  /** Extra styling passed to the outer pagination Box. */
  sx?: SxProps<Theme>;
}

/** Page-number list with leading/trailing ellipses for long ranges. */
const getPageNumbers = (current: number, total: number, delta = 1): (number | string)[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const range: number[] = [];
  const rangeWithDots: (number | string)[] = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  let prev = 0;
  for (const i of range) {
    if (prev && i - prev > 1) {
      rangeWithDots.push('…');
    }
    rangeWithDots.push(i);
    prev = i;
  }

  return rangeWithDots;
};

const navButtonSx = (theme: Theme) => ({
  textTransform: 'none' as const,
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 2,
  px: 1.75,
  py: 0.875,
  minWidth: 'auto',
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  transition: 'background-color .15s, border-color .15s, color .15s',
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
    borderColor: theme.palette.grey[400],
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.grey[200],
    color: theme.palette.text.disabled,
  },
});

const pageButtonSx = (theme: Theme, isActive: boolean) => ({
  width: 36,
  height: 36,
  minWidth: 36,
  borderRadius: 2,
  fontSize: 13,
  fontWeight: isActive ? 700 : 500,
  border: isActive ? 'none' : `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: isActive ? theme.palette.text.primary : theme.palette.background.paper,
  color: isActive ? theme.palette.background.paper : theme.palette.text.primary,
  boxShadow: 'none',
  transition: 'background-color .15s, color .15s, transform .1s',
  '&:hover': {
    backgroundColor: isActive ? theme.palette.text.primary : theme.palette.grey[200],
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: isActive ? theme.palette.text.disabled : theme.palette.grey[100],
    color: theme.palette.background.paper,
    border: 'none',
  },
});

/**
 * App-wide list/table pagination: Previous / page-numbers (with ellipses) /
 * Next, plus an optional right-aligned summary. Works with both server-driven
 * tables (pass `hasNextPage`/`hasPrevPage` from API meta) and client-side lists
 * (omit them — they default from `page`/`totalPages`).
 */
export const ListPagination: FC<ListPaginationProps> = observer(
  ({
    page,
    totalPages,
    onPageChange,
    isLoading = false,
    hasPrevPage,
    hasNextPage,
    summary,
    sx,
  }) => {
    const theme = useTheme();
    const pageNumbers = getPageNumbers(page, totalPages);
    const canPrev = hasPrevPage ?? page > 1;
    const canNext = hasNextPage ?? page < totalPages;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2.5,
          px: 0.5,
          gap: 2,
          flexWrap: 'wrap',
          ...sx,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => onPageChange(page - 1)}
            disabled={!canPrev || isLoading}
            startIcon={<CaretLeftIcon size={14} weight="bold" />}
            sx={navButtonSx(theme)}
          >
            <Trans i18nKey="app.common.previous" />
          </Button>

          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {pageNumbers.map((item, index) => {
              if (item === '…') {
                return (
                  <Typography
                    key={`ellipsis-${index}`}
                    sx={{
                      px: 0.5,
                      color: theme.palette.text.tertiary,
                      fontWeight: 500,
                      fontSize: 14,
                      userSelect: 'none',
                    }}
                  >
                    …
                  </Typography>
                );
              }

              const pageNum = item as number;
              const isActive = pageNum === page;
              return (
                <IconButton
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  disabled={isLoading}
                  disableRipple={isActive}
                  sx={pageButtonSx(theme, isActive)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {pageNum}
                </IconButton>
              );
            })}
          </Box>

          <Button
            variant="contained"
            disableElevation
            onClick={() => onPageChange(page + 1)}
            disabled={!canNext || isLoading}
            endIcon={<CaretRightIcon size={14} weight="bold" />}
            sx={navButtonSx(theme)}
          >
            <Trans i18nKey="app.common.next" />
          </Button>
        </Box>

        {summary && (
          <Typography
            sx={{
              color: theme.palette.text.tertiary,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {summary}
          </Typography>
        )}
      </Box>
    );
  },
);
